/**
 * S4.1 — „Was MAESTRO über Ihre Arbeitsweise weiß" (docs/LOOP-GEDAECHTNIS.md, Stufe 4).
 *
 * Das Bediener-Profil (`user_profiles`, sql/166) geht seit S1.5 in JEDEN Chat-Turn und in jeden
 * Angebots-Entwurf. Sichtbar war es nie, korrigierbar nie, löschbar nie — und geschrieben hat es
 * bis sql/241 überhaupt niemand. Diese Karte macht daraus, was das Kriterium verlangt: einsehbar,
 * korrigierbar, löschbar, an der Stelle, an der der Betreiber ohnehin nachsieht, was MAESTRO weiß.
 *
 * Drei Zusagen, die im Text stehen und nicht nur im Kommentar:
 *  - **Nur Sie sehen das.** RLS lässt seit sql/166 ausschließlich die eigene Zeile durch, auch für
 *    Inhaber. Eine Fläche, auf der Vorgesetzte Profile ihrer Leute läsen, wäre eine
 *    Überwachungsanzeige — nicht das, was hier gebaut wird.
 *  - **Es beschreibt, es bewertet nicht.** Keine Note, keine Ampel, keine Prognose über einen
 *    Menschen. Das ist strukturell abgesichert: der einzige Schreiber ist der Mensch selbst
 *    (sql/241 nimmt die Nutzer-Id aus dem Token), kein Modell und kein Verdichtungslauf.
 *  - **Löschen ist Löschen.** Ein eigener Knopf mit Rückfrage; ein versehentlich geleertes
 *    Textfeld löscht ausdrücklich NICHTS (die Route weist leeren Text mit `leer` ab).
 */
import { useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, ErrorNote, Icon, Textarea, Card } from "./components";

interface BedienerProfil {
  inhalt: string;
  version: number;
  updatedAt: string;
}
interface Antwort {
  profil: BedienerProfil | null;
  maxZeichen: number;
}

const PFAD = "/api/gedaechtnis/bediener-profil";

export function BedienerProfilKarte() {
  const { data, reload } = useApi<Antwort>(PFAD);
  const profil = data?.profil ?? null;
  const maxZeichen = data?.maxZeichen ?? 2000;

  const [bearbeiten, setBearbeiten] = useState(false);
  const [entwurf, setEntwurf] = useState("");
  const [loeschFrage, setLoeschFrage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  function starteBearbeiten() {
    setEntwurf(profil?.inhalt ?? "");
    setFehler(null);
    setLoeschFrage(false);
    setBearbeiten(true);
  }

  async function speichern() {
    const inhalt = entwurf.trim();
    if (!inhalt) {
      // Bewusst KEINE stille Löschung: dafür gibt es den Löschknopf, mit Rückfrage.
      setFehler("Ein leerer Text löscht nichts — dafür gibt es den Löschknopf.");
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(PFAD, "PUT", { inhalt });
      setBearbeiten(false);
      reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Das Profil konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  }

  async function loeschen() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(PFAD, "DELETE");
      setLoeschFrage(false);
      setBearbeiten(false);
      reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Das Profil konnte nicht gelöscht werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" className="mb-4 p-5">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
            <Icon name="badge" className="text-[18px]" />
          </span>
          <div>
            <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
              Was MAESTRO über Ihre Arbeitsweise weiß
            </p>
            <p className="font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
              Das sehen nur Sie — auch Inhaber Ihres Betriebs nicht. Es beschreibt, wie Sie arbeiten;
              es bewertet Sie nicht.
            </p>
          </div>
        </div>
        {!bearbeiten && (
          <div className="flex shrink-0 gap-2">
            <Button type="button" variant="secondary" onClick={starteBearbeiten} disabled={busy}>
              <Icon name="edit" className="text-[18px]" /> {profil ? "Bearbeiten" : "Notiz hinzufügen"}
            </Button>
            {profil && (
              <Button type="button" variant="ghost" onClick={() => setLoeschFrage(true)} disabled={busy}>
                <Icon name="delete" className="text-[18px]" /> Löschen
              </Button>
            )}
          </div>
        )}
      </div>

      {fehler && <ErrorNote message={fehler} />}

      {bearbeiten ? (
        <div className="mt-3">
          <Textarea
            rows={5}
            maxLength={maxZeichen}
            aria-label="Ihre Arbeitsweise"
            placeholder={"z. B. „Ich arbeite Anfragen meist abends durch“ oder „Bitte kurze Antworten, keine Aufzählungen“."}
            value={entwurf}
            onChange={(e) => setEntwurf(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-label-md text-label-md text-on-surface-variant">
              {entwurf.length}/{maxZeichen} Zeichen
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => setBearbeiten(false)} disabled={busy}>
                Abbrechen
              </Button>
              <Button type="button" variant="primary" onClick={() => void speichern()} disabled={busy}>
                Speichern
              </Button>
            </div>
          </div>
        </div>
      ) : loeschFrage ? (
        <div className="mt-3 rounded-xl border border-outline-variant/50 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
          <p className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            Diese Notiz wird endgültig entfernt. MAESTRO weiß danach nichts mehr über Ihre Arbeitsweise — Ihre
            Vorgänge und das Wissen über den Betrieb bleiben unberührt.
          </p>
          <div className="mt-3 flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setLoeschFrage(false)} disabled={busy}>
              Abbrechen
            </Button>
            <Button type="button" variant="primary" onClick={() => void loeschen()} disabled={busy}>
              Endgültig löschen
            </Button>
          </div>
        </div>
      ) : profil ? (
        <p className="mt-1 whitespace-pre-wrap font-body-sm text-body-sm leading-relaxed text-on-surface dark:text-inverse-on-surface">
          {profil.inhalt}
        </p>
      ) : (
        <p className="mt-1 font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
          MAESTRO hat noch nichts über Ihre Arbeitsweise notiert. Sie können selbst etwas hinterlegen — der Assistent
          berücksichtigt es dann in jedem Gespräch und in jedem Angebots-Entwurf.
        </p>
      )}
    </Card>
  );
}
